import { esc, matchPlaces, type SearchPlace } from '@puurweer/shared';

/**
 * Zoek-combobox-island (§6): verrijkt de header-zoekbalk tot een ARIA-combobox met een
 * live listbox, volledig toetsenbord-bedienbaar. Matcht client-side via de **gedeelde**
 * `matchPlaces` (zelfde logica als de `/zoek`-SSR → geen drift).
 *
 * Progressive enhancement: zonder JS submit de header-form naar `/zoek` (no-JS pad). De
 * plaatsenlijst wordt **lazy** bij de eerste focus geladen (niet in de initiële payload).
 * Een keuze onthoudt de slug functioneel in localStorage (geen cookie) en navigeert
 * naar `/<slug>`. Importeert gericht uit shared (sideEffects:false → geen zod in de bundel).
 */

const STORAGE_KEY = 'puurweer.plaats';
// F6: alleen veilige slugs ([a-z0-9-]) → geen path-traversal/open-redirect via getamperde localStorage.
const SLUG_RE = /^[a-z0-9-]+$/;

/**
 * Home-restore (§6): op `/` meteen door naar je eerder gekozen plaats, zodat je laatste keuze je
 * standaardplek wordt. No-op zonder opslag, op andere paden, of als er geen plaats getoond wordt
 * (geen `#place-name[data-slug]`). Geeft `true` terug als 'ie navigeert.
 */
function maybeRestoreHome(): boolean {
  if (location.pathname !== '/') return false;
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch {
    return false; // localStorage uit/onbeschikbaar → niets te herstellen
  }
  if (!saved || !SLUG_RE.test(saved)) return false;
  const shown = document.getElementById('place-name')?.dataset.slug;
  if (!shown || saved === shown) return false; // geen plaats getoond, of al de juiste
  location.replace(`/${saved}`);
  return true;
}

function initSearch(): void {
  const inputEl = document.getElementById('q');
  const listEl = document.getElementById('lb');
  if (!(inputEl instanceof HTMLInputElement) || !listEl) return;
  // Herbind als const met de gesmalde types, zodat de closures hieronder ze kennen.
  const input = inputEl;
  const listbox = listEl;

  let index: SearchPlace[] | null = null;
  let options: SearchPlace[] = [];
  let active = -1;

  async function loadIndex(): Promise<void> {
    if (index) return;
    try {
      const res = await fetch('/places.json');
      index = (await res.json()) as SearchPlace[];
    } catch {
      index = []; // stil falen; de form-submit naar /zoek blijft het no-JS-pad
    }
    runQuery(); // toon meteen resultaten voor wat al getypt is
  }

  function close(): void {
    listbox.classList.remove('open');
    listbox.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    options = [];
    active = -1;
  }

  function render(): void {
    listbox.innerHTML =
      options.length === 0
        ? '<li class="empty">Geen plaats gevonden</li>'
        : options
            .map(
              (p, i) =>
                `<li role="option" id="opt${i}" aria-selected="${i === active}" data-i="${i}">${esc(p.name)} <small>${esc(p.province)}</small></li>`,
            )
            .join('');
    listbox.classList.add('open');
    input.setAttribute('aria-expanded', 'true');
  }

  function setActive(i: number): void {
    active = i;
    const items = listbox.querySelectorAll('[role=option]');
    items.forEach((li, j) => {
      li.setAttribute('aria-selected', String(j === active));
    });
    if (active >= 0) input.setAttribute('aria-activedescendant', `opt${active}`);
    else input.removeAttribute('aria-activedescendant');
  }

  function runQuery(): void {
    const q = input.value.trim();
    if (!q) {
      close();
      return;
    }
    if (!index) return; // nog aan het laden — render zodra de index binnen is
    options = matchPlaces(index, q, 8);
    active = -1;
    render();
  }

  function choose(place: SearchPlace): void {
    try {
      localStorage.setItem(STORAGE_KEY, place.slug);
    } catch {
      // localStorage uit/onbeschikbaar → niet erg, navigeren kan alsnog
    }
    location.assign(`/${place.slug}`);
  }

  input.addEventListener('focus', loadIndex, { once: true });
  input.addEventListener('input', runQuery);

  input.addEventListener('keydown', (event: KeyboardEvent) => {
    if (!listbox.classList.contains('open') || options.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((active + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((active - 1 + options.length) % options.length);
    } else if (event.key === 'Enter') {
      const chosen = options[active];
      if (chosen) {
        event.preventDefault();
        choose(chosen);
      }
      // geen selectie → laat de form submitten naar /zoek (no-JS resultatenpagina)
    } else if (event.key === 'Escape') {
      close();
    }
  });

  listbox.addEventListener('click', (event) => {
    const li = event.target instanceof Element ? event.target.closest('[data-i]') : null;
    if (!li) return;
    const chosen = options[Number(li.getAttribute('data-i'))];
    if (chosen) choose(chosen);
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('.search')) close();
  });
}

// Eerst de home-restore (vóór de search-init); bij een redirect heeft initialiseren geen zin.
if (!maybeRestoreHome()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearch);
  } else {
    initSearch();
  }
}

export { initSearch, maybeRestoreHome };
